// Frontend deprecation detection and warning system

interface DeprecationHeaders {
  'x-deprecated'?: string;
  'x-deprecation-reason'?: string;
  'x-deprecation-alternative'?: string;
  'x-deprecation-since'?: string;
  'x-deprecation-remove-in'?: string;
}

export function checkForDeprecationHeaders(response: any) {
  const headers = response.headers as DeprecationHeaders;
  
  if (headers['x-deprecated'] === 'true') {
    const warning = [
      '🚨 DEPRECATED API ENDPOINT USED',
      '━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━',
      `🚫 Reason: ${headers['x-deprecation-reason'] || 'Not specified'}`,
      `✅ Alternative: ${headers['x-deprecation-alternative'] || 'Not specified'}`,
      headers['x-deprecation-since'] ? `📅 Deprecated since: v${headers['x-deprecation-since']}` : '',
      headers['x-deprecation-remove-in'] ? `⚠️  Will be removed in: v${headers['x-deprecation-remove-in']}` : '',
      `📍 URL: ${response.config?.url || 'unknown'}`,
      `🕒 Timestamp: ${new Date().toISOString()}`,
      '━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━',
      '🔧 Please update your code to use the recommended alternative!'
    ].filter(Boolean).join('\n');

    // Log warning in bright red
    console.error('%c' + warning, 'color: #ff4444; font-weight: bold;');
    
    // Show a toast notification in development
    if (process.env.NODE_ENV === 'development') {
      showDeprecationToast(headers);
    }
  }
}

function showDeprecationToast(headers: DeprecationHeaders) {
  // Create a temporary toast notification
  const toast = document.createElement('div');
  toast.style.cssText = `
    position: fixed;
    top: 20px;
    right: 20px;
    background: #ff4444;
    color: white;
    padding: 16px;
    border-radius: 8px;
    font-family: monospace;
    font-size: 12px;
    max-width: 400px;
    z-index: 10000;
    box-shadow: 0 4px 12px rgba(0,0,0,0.3);
    border-left: 4px solid #ff0000;
  `;
  
  toast.innerHTML = `
    <div style="font-weight: bold; margin-bottom: 8px;">🚨 DEPRECATED API USED</div>
    <div><strong>Reason:</strong> ${headers['x-deprecation-reason'] || 'Not specified'}</div>
    <div><strong>Alternative:</strong> ${headers['x-deprecation-alternative'] || 'Not specified'}</div>
    ${headers['x-deprecation-remove-in'] ? `<div><strong>Removal:</strong> v${headers['x-deprecation-remove-in']}</div>` : ''}
    <div style="margin-top: 8px; font-size: 10px; opacity: 0.8;">Check console for details</div>
  `;
  
  document.body.appendChild(toast);
  
  // Auto-remove after 10 seconds
  setTimeout(() => {
    if (toast.parentNode) {
      toast.parentNode.removeChild(toast);
    }
  }, 10000);
  
  // Click to dismiss
  toast.addEventListener('click', () => {
    if (toast.parentNode) {
      toast.parentNode.removeChild(toast);
    }
  });
}

export function logDeprecatedEndpointUsage(endpoint: string, reason: string, alternative: string) {
  const warning = [
    '🚨 DEPRECATED FRONTEND CODE',
    '━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━',
    `📍 Endpoint: ${endpoint}`,
    `🚫 Reason: ${reason}`,
    `✅ Alternative: ${alternative}`,
    `🕒 Timestamp: ${new Date().toISOString()}`,
    '━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━'
  ].join('\n');

  console.error('%c' + warning, 'color: #ff4444; font-weight: bold;');
}
